import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip
} from '@mui/material';

const sectionTitle = {
  fontSize: '0.8rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'text.primary',
};

const RiskHeatMap = ({ data }) => {
  // Ensure data is an array
  const heatmapData = Array.isArray(data) ? data : [];

  if (!data || heatmapData.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ p: 2.5 }}>
          <Typography sx={sectionTitle} gutterBottom>Risk Distribution Heat Map</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
            <Typography variant="body2" color="text.secondary">No heat map data available</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const getRiskColor = (riskScore) => {
    if (riskScore >= 8) return '#d27373'; // Critical
    if (riskScore >= 6) return '#d4925e'; // High
    if (riskScore >= 4) return '#c9a84a'; // Moderate
    return '#5da87a'; // Low/Appropriate
  };

  const getRiskBgColor = (riskScore) => {
    if (riskScore >= 8) return 'rgba(210, 115, 115, 0.15)';
    if (riskScore >= 6) return 'rgba(212, 146, 94, 0.15)';
    if (riskScore >= 4) return 'rgba(201, 168, 74, 0.15)';
    return 'rgba(93, 168, 122, 0.15)';
  };

  const getRiskLabel = (riskScore) => {
    if (riskScore >= 8) return 'Critical';
    if (riskScore >= 6) return 'High';
    if (riskScore >= 4) return 'Moderate';
    return 'Low';
  };

  // Sort by avg_risk_score descending
  const sortedData = [...heatmapData].sort((a, b) =>
    (b.avg_risk_score || 0) - (a.avg_risk_score || 0)
  );

  // Limit to top 15 for readability
  const displayData = sortedData.slice(0, 15);

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: 2.5, pb: 0 }}>
        <Typography sx={sectionTitle} gutterBottom>Risk Distribution Heat Map</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Top 15 department/role combinations by risk score
        </Typography>
      </CardContent>
      <Box sx={{ flex: 1, px: 1, pb: 1 }}>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Department</TableCell>
                <TableCell>Job Title</TableCell>
                <TableCell align="center">Users</TableCell>
                <TableCell align="center">Avg Risk</TableCell>
                <TableCell align="center">Risk Level</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayData.map((item, index) => {
                const avgRiskScore = item.avg_risk_score || 0;
                const riskColor = getRiskColor(avgRiskScore);
                const riskBgColor = getRiskBgColor(avgRiskScore);
                const riskLabel = getRiskLabel(avgRiskScore);

                return (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {item.department || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.job_title || 'Unknown'}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={item.user_count} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: riskColor }}>
                        {avgRiskScore.toFixed(1)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={riskLabel}
                        size="small"
                        sx={{
                          bgcolor: riskBgColor,
                          color: riskColor,
                          border: `1px solid ${riskColor}`,
                          fontWeight: 'bold'
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {sortedData.length > 15 && (
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Showing top 15 of {sortedData.length} combinations
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default RiskHeatMap;
